import { formatKpiValue } from '../utils';
import {
  formatCurrencyFr, formatDaysFr, formatNumberFr, formatPercentageFr,
} from '@/lib/formatters';

const formatters = { formatCurrencyFr, formatDaysFr, formatNumberFr, formatPercentageFr };

const MONTH_SHORT = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin',
                     'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];

const BORDEAUX = '#8B0028';
const GOLD = '#B5862A';
const BORDER = '#E8DCC4';
const INK_SOFT = '#9A8E80';
const CREAM_DARK = '#F4EFE5';

/**
 * Mini-graphique d'évolution 12 mois — SVG natif, viewBox 600×140.
 * Trait plein bordeaux pour l'officine, ligne horizontale pointillée gold
 * pour le benchmark (médiane cohorte au mois courant).
 *
 * Props :
 *   - series: [{period: "YYYY-MM", value: number|null}, ...] ordre chronologique
 *   - benchmarkMedian: number|null (constant horizontal sur la période)
 *   - kpi: {label, unit, format_type} pour formatter les tooltips/last value
 */
export default function MiniChart12Months({ series = [], benchmarkMedian, kpi, height = 140 }) {
  const W = 600;
  const H = height;
  const PAD_TOP = 22;
  const PAD_BOTTOM = 26;
  const PAD_X = 14;

  if (!series || series.length === 0) {
    return (
      <div className="bg-cabinet-paper border border-cabinet-border p-6 text-center" data-testid="mini-chart-empty">
        <p className="font-playfair italic text-[12px] text-cabinet-ink-soft leading-[1.7]">
          Pas encore d'historique disponible. Le graphique s'enrichira au fil de vos saisies mensuelles.
        </p>
      </div>
    );
  }

  // Find min/max from valid values + benchmark, with safe padding
  const validValues = series.map((s) => s.value).filter((v) => typeof v === 'number');
  const allValues = [...validValues];
  if (typeof benchmarkMedian === 'number') allValues.push(benchmarkMedian);

  const min = allValues.length ? Math.min(...allValues) : 0;
  const max = allValues.length ? Math.max(...allValues) : 1;
  const range = max - min || Math.max(1, Math.abs(max));
  const yMin = min - range * 0.15;
  const yMax = max + range * 0.15;
  const yRange = (yMax - yMin) || 1;

  const xFor = (i) => PAD_X + (i * (W - 2 * PAD_X)) / Math.max(1, series.length - 1);
  const yFor = (v) => PAD_TOP + (1 - (v - yMin) / yRange) * (H - PAD_TOP - PAD_BOTTOM);

  // Build the polyline for valid points only — ignore null gaps (don't connect
  // through them to avoid misleading interpolation).
  const segments = [];
  let current = [];
  series.forEach((s, i) => {
    if (typeof s.value === 'number') {
      current.push({ x: xFor(i), y: yFor(s.value), period: s.period, value: s.value });
    } else if (current.length) {
      segments.push(current);
      current = [];
    }
  });
  if (current.length) segments.push(current);

  const lastValid = [...series].reverse().find((s) => typeof s.value === 'number');
  const lastIdx = lastValid ? series.findIndex((s) => s.period === lastValid.period) : -1;

  const benchmarkY = typeof benchmarkMedian === 'number' ? yFor(benchmarkMedian) : null;

  // Display 5 evenly-spaced month labels on the x axis
  const labelIndexes = series.length <= 5
    ? series.map((_, i) => i)
    : [0, Math.round((series.length - 1) * 0.25), Math.round((series.length - 1) * 0.5),
       Math.round((series.length - 1) * 0.75), series.length - 1];

  return (
    <div className="bg-cabinet-paper border border-cabinet-border p-5" data-testid="mini-chart-12-months">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.22em] font-medium mb-0.5">
            Évolution 12 mois
          </div>
          <div className="font-playfair text-[14px] text-cabinet-ink leading-snug">
            {kpi?.label || '—'}
          </div>
        </div>
        {lastValid && (
          <div className="text-right">
            <div className="text-[9px] text-cabinet-ink-soft uppercase tracking-[0.18em]">Dernier mois</div>
            <div className="font-playfair text-[16px] text-cabinet-bordeaux mt-0.5">
              {formatKpiValue({ ...kpi, value: lastValid.value }, formatters)}
            </div>
          </div>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height: `${height}px` }}
        role="img"
        aria-label={`Évolution 12 mois de ${kpi?.label || 'l\'indicateur'}`}
      >
        {/* Background frame */}
        <rect x="0" y="0" width={W} height={H} fill={CREAM_DARK} opacity="0.4" />

        {/* Benchmark horizontal dashed line */}
        {benchmarkY != null && (
          <>
            <line
              x1={PAD_X} y1={benchmarkY} x2={W - PAD_X} y2={benchmarkY}
              stroke={GOLD} strokeWidth="1.5" strokeDasharray="5,3"
            />
            <text
              x={W - PAD_X} y={benchmarkY - 5}
              textAnchor="end" fontFamily="Manrope, sans-serif"
              fontSize="9" fontWeight="600" fill={GOLD}
            >
              Benchmark · {formatKpiValue({ ...kpi, value: benchmarkMedian }, formatters)}
            </text>
          </>
        )}

        {/* Officine polyline (segments to avoid bridging null gaps) */}
        {segments.map((seg, idx) => (
          <polyline
            key={idx}
            points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none" stroke={BORDEAUX} strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round"
          />
        ))}

        {/* Vertices */}
        {segments.map((seg) =>
          seg.map((p, i) => (
            <circle
              key={`${p.period}-${i}`}
              cx={p.x} cy={p.y}
              r={lastIdx >= 0 && p.period === series[lastIdx].period ? 4.5 : 2.5}
              fill={BORDEAUX} stroke="#FBF8F3" strokeWidth="1.5"
            />
          ))
        )}

        {/* X-axis tick labels */}
        {labelIndexes.map((i) => {
          const m = parseInt(series[i].period.split('-')[1], 10) - 1;
          return (
            <text
              key={i}
              x={xFor(i)} y={H - 8}
              textAnchor="middle" fontFamily="Manrope, sans-serif"
              fontSize="9" fill={INK_SOFT}
            >
              {MONTH_SHORT[m] || series[i].period}
            </text>
          );
        })}
      </svg>

      <div className="mt-3 flex items-center gap-4 text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.18em]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5" style={{ background: BORDEAUX }} aria-hidden /> Vous
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-px border-t border-dashed"
            style={{ borderColor: GOLD }}
            aria-hidden
          /> Benchmark
        </span>
      </div>
    </div>
  );
}
